/**
 * AwsS3ExportScheduledClickConversionLogsPage
 *
 * Scheduled S3 export rules for click + conversion logs.
 *  - Create rule: export_id, s3_bucket, s3_prefix, cadence (cron or preset),
 *    log_type (clicks|conversions|both), format (csv|ndjson|parquet),
 *    partition-by-date toggle, optional filter.
 *  - Toggle enabled / Run now / Delete per row.
 *  - Recent runs table: last_run_at + last_status + next_run_at.
 *
 * Visual: GREEN-only accent (green-600/700), zinc borders, 6-8px rounds.
 * No emerald, no sky, no purple, no orange, no emoji.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAwsS3ExportScheduledClickConversionLogsCreate,
	useAwsS3ExportScheduledClickConversionLogsDelete,
	useAwsS3ExportScheduledClickConversionLogsList,
	useAwsS3ExportScheduledClickConversionLogsPatch,
	useAwsS3ExportScheduledClickConversionLogsRunNow,
} from "./hooks";
import {
	cadenceToCron,
	parseCron,
	type Cadence,
	type LogType,
	type ScheduleRow,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_RUN =
	"rounded-[6px] border border-green-700 bg-white px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-medium text-zinc-600";

const CADENCES: Cadence[] = ["hourly", "daily", "weekly"];
const LOG_TYPES: LogType[] = ["clicks", "conversions", "both"];
const FORMATS = ["csv", "ndjson", "parquet"] as const;

type FormatT = (typeof FORMATS)[number];

function fmtEpoch(sec: number | null): string {
	if (sec === null || sec === undefined) return "never";
	try {
		const d = new Date(sec * 1000);
		return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
	} catch {
		return String(sec);
	}
}

function statusLabel(s: ScheduleRow["last_status"]): string {
	if (s === "success") return "ok";
	if (s === "failed") return "failed";
	if (s === "pending") return "pending";
	return "never ran";
}

function statusClass(s: ScheduleRow["last_status"]): string {
	if (s === "success")
		return "rounded-[6px] border border-green-700 bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-800";
	if (s === "failed")
		return "rounded-[6px] border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700";
	if (s === "pending")
		return "rounded-[6px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700";
	return "rounded-[6px] border border-zinc-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-zinc-500";
}

export function AwsS3ExportScheduledClickConversionLogsPage() {
	const listQuery = useAwsS3ExportScheduledClickConversionLogsList();
	const createMut = useAwsS3ExportScheduledClickConversionLogsCreate();
	const patchMut = useAwsS3ExportScheduledClickConversionLogsPatch();
	const runNowMut = useAwsS3ExportScheduledClickConversionLogsRunNow();
	const deleteMut = useAwsS3ExportScheduledClickConversionLogsDelete();

	const [exportId, setExportId] = useState("");
	const [bucket, setBucket] = useState("");
	const [prefix, setPrefix] = useState("");
	const [cronExpr, setCronExpr] = useState("0 * * * *");
	const [logType, setLogType] = useState<LogType>("clicks");
	const [format, setFormat] = useState<FormatT>("csv");
	const [partition, setPartition] = useState(true);
	const [filter, setFilter] = useState("");

	const cronParsed = useMemo(() => parseCron(cronExpr), [cronExpr]);

	const items = listQuery.data?.items ?? [];

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!exportId.trim() || !bucket.trim() || !cronParsed.ok || !cronParsed.cadence) return;
		createMut.mutate(
			{
				export_id: exportId.trim(),
				s3_bucket: bucket.trim(),
				s3_prefix: prefix.trim(),
				cadence: cronParsed.cadence,
				log_type: logType,
				date_range: {
					format,
					partition_by_date: partition,
					filter: filter.trim() || undefined,
				},
				enabled: true,
			},
			{
				onSuccess: () => {
					setExportId("");
					setBucket("");
					setPrefix("");
					setFilter("");
				},
			},
		);
	};

	const disableCreate =
		createMut.isPending ||
		!exportId.trim() ||
		!bucket.trim() ||
		!cronParsed.ok ||
		!cronParsed.cadence;

	return (
		<main
			data-testid="aws-s3-export-scheduled-click-conversion-logs-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					aws-s3-export-scheduled-click-conversion-logs
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Scheduled S3 log export
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Push click and conversion logs to S3 on a cron. Credentials are not
					accepted here. Access is assumed via IAM role.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Create scheduled export rule"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					New export rule
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
					data-testid="aws-s3-export-scheduled-click-conversion-logs-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>export_id</span>
						<input
							type="text"
							className={INPUT}
							placeholder="my-nightly-clicks"
							value={exportId}
							onChange={(e) => setExportId(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-export-id"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>s3_bucket</span>
						<input
							type="text"
							className={INPUT}
							placeholder="my-bucket"
							value={bucket}
							onChange={(e) => setBucket(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-bucket"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>s3_prefix</span>
						<input
							type="text"
							className={INPUT}
							placeholder="exports/clicks/"
							value={prefix}
							onChange={(e) => setPrefix(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-prefix"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>schedule (cron)</span>
						<input
							type="text"
							className={INPUT}
							placeholder="0 * * * *"
							value={cronExpr}
							onChange={(e) => setCronExpr(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-cron"
						/>
						<span
							className="text-[11px] text-zinc-500"
							data-testid="aws-s3-export-scheduled-click-conversion-logs-cron-hint"
						>
							{cronParsed.ok && cronParsed.cadence
								? `parsed: ${cronParsed.cadence}`
								: `invalid: ${cronParsed.reason ?? "bad cron"}`}
						</span>
						<div className="mt-1 flex flex-wrap gap-1.5">
							{CADENCES.map((c) => (
								<button
									type="button"
									key={c}
									className={BTN_GHOST}
									onClick={() => setCronExpr(cadenceToCron(c, 0, 0, 1))}
									data-testid={`aws-s3-export-scheduled-click-conversion-logs-cadence-${c}`}
								>
									{c}
								</button>
							))}
						</div>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>log_type</span>
						<select
							className={SELECT}
							value={logType}
							onChange={(e) => setLogType(e.currentTarget.value as LogType)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-logtype"
						>
							{LOG_TYPES.map((lt) => (
								<option key={lt} value={lt}>
									{lt}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>format</span>
						<select
							className={SELECT}
							value={format}
							onChange={(e) => setFormat(e.currentTarget.value as FormatT)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-format"
						>
							{FORMATS.map((f) => (
								<option key={f} value={f}>
									{f}
								</option>
							))}
						</select>
					</label>
					<label className="flex items-center gap-2 sm:col-span-1">
						<input
							type="checkbox"
							checked={partition}
							onChange={(e) => setPartition(e.currentTarget.checked)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-partition"
							className="h-4 w-4 rounded-[4px] border border-zinc-300 accent-green-700"
						/>
						<span className="text-sm text-zinc-700">
							partition by date (YYYY/MM/DD)
						</span>
					</label>
					<label className="flex flex-col gap-1 sm:col-span-2">
						<span className={LABEL}>filter (optional)</span>
						<input
							type="text"
							className={INPUT}
							placeholder={`country="US" AND event="purchase"`}
							value={filter}
							onChange={(e) => setFilter(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-filter"
						/>
					</label>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={disableCreate}
							data-testid="aws-s3-export-scheduled-click-conversion-logs-submit"
						>
							{createMut.isPending ? "Saving..." : "Create rule"}
						</button>
						{createMut.error ? (
							<p className="mt-2 text-xs text-red-600" role="alert">
								{createMut.error.message}
							</p>
						) : null}
					</div>
				</form>
			</section>

			<section
				className={CARD}
				aria-label="Scheduled export rules"
			>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Rules ({items.length})
					</h2>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="aws-s3-export-scheduled-click-conversion-logs-loading"
					>
						Loading...
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="aws-s3-export-scheduled-click-conversion-logs-empty"
					>
						No scheduled rules yet. Create one above.
					</p>
				) : (
					<div
						className="mt-3 overflow-x-auto"
						data-testid="aws-s3-export-scheduled-click-conversion-logs-list"
					>
						<table className="w-full text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
									<th className="py-2 pr-3 font-medium">export_id</th>
									<th className="py-2 pr-3 font-medium">bucket / prefix</th>
									<th className="py-2 pr-3 font-medium">cadence</th>
									<th className="py-2 pr-3 font-medium">log_type</th>
									<th className="py-2 pr-3 font-medium">enabled</th>
									<th className="py-2 pr-3 font-medium">last run</th>
									<th className="py-2 pr-3 font-medium">next run</th>
									<th className="py-2 pr-3 font-medium">actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => (
									<tr
										key={row.id}
										data-testid={`aws-s3-export-scheduled-click-conversion-logs-row-${row.id}`}
										className="align-top"
									>
										<td className="py-2 pr-3">
											<div className="font-medium text-zinc-900">
												{row.export_id}
											</div>
											<div className="font-mono text-[10px] text-zinc-500">
												{row.id}
											</div>
										</td>
										<td className="py-2 pr-3">
											<div className="text-zinc-900">{row.s3_bucket}</div>
											<div className="text-[11px] text-zinc-500">
												{row.s3_prefix || "(root)"}
											</div>
										</td>
										<td className="py-2 pr-3 text-zinc-700">{row.cadence}</td>
										<td className="py-2 pr-3 text-zinc-700">{row.log_type}</td>
										<td className="py-2 pr-3">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() =>
													patchMut.mutate({
														id: row.id,
														body: { enabled: !row.enabled },
													})
												}
												disabled={patchMut.isPending}
												data-testid={`aws-s3-export-scheduled-click-conversion-logs-toggle-${row.id}`}
											>
												{row.enabled ? "on" : "off"}
											</button>
										</td>
										<td className="py-2 pr-3">
											<div className="flex flex-col gap-0.5">
												<span className="text-[11px] text-zinc-600">
													{fmtEpoch(row.last_run_at)}
												</span>
												<span className={statusClass(row.last_status)}>
													{statusLabel(row.last_status)}
												</span>
											</div>
										</td>
										<td className="py-2 pr-3 text-[11px] text-zinc-600">
											{fmtEpoch(row.next_run_at)}
										</td>
										<td className="py-2 pr-3">
											<div className="flex gap-1.5">
												<button
													type="button"
													className={BTN_RUN}
													onClick={() => runNowMut.mutate(row.id)}
													disabled={runNowMut.isPending}
													data-testid={`aws-s3-export-scheduled-click-conversion-logs-runnow-${row.id}`}
												>
													Run now
												</button>
												<button
													type="button"
													className={BTN_GHOST}
													onClick={() => deleteMut.mutate(row.id)}
													disabled={deleteMut.isPending}
													data-testid={`aws-s3-export-scheduled-click-conversion-logs-delete-${row.id}`}
												>
													Delete
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section
				className={CARD}
				aria-label="Recent runs"
			>
				<h2 className="text-base font-semibold text-zinc-900">Recent runs</h2>
				{items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="aws-s3-export-scheduled-click-conversion-logs-runs-empty"
					>
						No runs yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-100"
						data-testid="aws-s3-export-scheduled-click-conversion-logs-runs"
					>
						{items
							.filter((r) => r.last_run_at !== null)
							.slice(0, 10)
							.map((r) => (
								<li
									key={r.id}
									className="flex items-center justify-between py-2 text-sm"
								>
									<span className="flex flex-col">
										<span className="font-medium text-zinc-900">
											{r.export_id}
										</span>
										<span className="text-[11px] text-zinc-500">
											{fmtEpoch(r.last_run_at)}
										</span>
									</span>
									<span className={statusClass(r.last_status)}>
										{statusLabel(r.last_status)}
									</span>
								</li>
							))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AwsS3ExportScheduledClickConversionLogsPage;
